import generateContext, { createAction } from '@shared/context';
import { ICourier } from '../couriers';

interface IOrdersState {
  selectedOrderId: any;
  clickedOrderId: any;
  ordersById: Dictionary<IOrder>;
  activeOrders: any;
}

enum OrderPaymentStatus {
  Paid = 'PAID',
  Unpaid = 'UNPAID',
  Unknown = 'UNKNOWN',
}


export interface IOrderDetailItems {
  additions:{
    id: string;
    name: string;
  }[],
  comment: string;
  id:string;
  name:string;
  quantity:string;
}

export enum OrderStatus {
  Assigned = 'ASSIGNED',
  Unassigned = 'UNASSIGNED',
  InDelivery = 'IN_DELIVERY',
  Delivered = 'DELIVERED',
  AssignedToNextPool = 'ASSIGNED_TO_NEXT_POOL'
}

export interface IOrder {
  timestamp: number;
  uuid: string;
  creationDate: string;
  expectedDeliveryTime: string;
  reference: string;
  orderDate: any;
  expireTime?: any; // TODO: Remove this when the backend part is updated;
  customer: {
    lastName: string;
    address: {
      street: string;
      streetNumber: string;
      postcode: string;
      city: string;
    };
  };
  courier: ICourier;
  comments: string;
  hasAlcohol: boolean;
  payment?: {
    totalAmount?: number;
    currencyCode?: string;
    status: OrderPaymentStatus;
  };
  location: Coordinates;
  platform: string;
  status: OrderStatus;
  updateDate: string;
  hash?: string;
  items?: IOrderDetailItems[]
}

export interface IExpiredOrder {
  uuid?: string;
  orderHash?: string;
  reason?: string;
}

enum OrdersActionType {
  SetOrders = 'SET_ORDERS',
  AddOrder = 'ADD_ORDER',
  UpdateOrder = 'UPDATE_ORDER',
  SetSelectedOrderId = 'SET_SELECTED_ORDER',
  ExpiredOrder = 'EXPIRED_ORDER',
  ActiveOrder = 'ACTIVE_ORDER',
  ClickedOrderId = 'SET_CLICKED_ORDER'
}

const initialState: IOrdersState = {
  selectedOrderId: null,
  clickedOrderId: null,
  ordersById: {},
  activeOrders: [],
};

const reducer = (
  state: IOrdersState,
  action: { type: OrdersActionType; payload?: any }
): IOrdersState => {
  switch (action.type) {
    case OrdersActionType.SetOrders: {
      const orderIds: any = [];
      const orderSnapshot = action.payload.reduce(
        (orderSnapshot: any, { ...orderItem }: IOrder) => {
          orderSnapshot[orderItem.uuid] = orderItem;
          orderIds.push(orderItem.uuid);
          return orderSnapshot;
        },
        {}
      );
      return {
        ...state,
        ordersById: {
          ...orderSnapshot,
        },
        activeOrders: [...orderIds],
      };
    }

    case OrdersActionType.AddOrder:
      return {
        ...state,
        ordersById: {
          ...state.ordersById,
          [action.payload.uuid]: action.payload,
        },
      };

    case OrdersActionType.UpdateOrder: {
      const currentOrder = state.ordersById[action.payload.uuid];
      if (
        !currentOrder ||
        Number(currentOrder.timestamp) > action.payload.timestamp
      ) {
        return state;
      }
      state.ordersById[action.payload.uuid] = action.payload;
      return {
        ...state,
        ordersById: {
          ...state.ordersById,
          [action.payload.uuid]: {
            ...state.ordersById[action.payload.uuid],
          },
        },
      };
    }

    case OrdersActionType.SetSelectedOrderId: {
      return {
        ...state,
        selectedOrderId: action.payload,
      };
    }

    case OrdersActionType.ClickedOrderId: {
      return {
        ...state,
        clickedOrderId: action.payload,
      };
    }

    case OrdersActionType.ExpiredOrder: {
      if (state.ordersById[action.payload.uuid]) {
        delete state.ordersById[action.payload.uuid];
      }
      return {
        ...state,
      };
    }

    default:
      return state;
  }
};

const [OrdersCtxProvider, useOrdersState, useOrdersDispatch] = generateContext(
  reducer,
  initialState
);

const useOrdersActions = () => {
  const dispatch = useOrdersDispatch();

  const setOrders = (orders: IOrder[]) => {
    const action = createAction(OrdersActionType.SetOrders, orders);
    dispatch(action);
  };

  const addOrder = (order: IOrder) => {
    const action = createAction(OrdersActionType.AddOrder, order);
    dispatch(action);
  };

  const setSelectedOrderId = (orderId: string | null) => {
    const action = createAction(OrdersActionType.SetSelectedOrderId, orderId);
    dispatch(action);
  };

  const setClickedOrderId = (orderId: string | null) => {
    const action = createAction(OrdersActionType.ClickedOrderId, orderId);
    dispatch(action);
  };

  const updateOrder = (order: IOrder) => {
    const action = createAction(OrdersActionType.UpdateOrder, order);
    dispatch(action);
  };

  const expiredOrder = (expiredOrder: IExpiredOrder) => {
    const action = createAction(OrdersActionType.ExpiredOrder, expiredOrder);
    dispatch(action);
  };

  return {
    setOrders,
    addOrder,
    updateOrder,
    setSelectedOrderId,
    setClickedOrderId,
    expiredOrder,
  };
};

export { OrdersCtxProvider, useOrdersState, useOrdersActions };
